import { useEffect, useMemo, useState } from "react";
import { ApiService } from "../../../../services/api";
import { MessageSquare, Send, Trash2, Clock } from "lucide-react";
import checkPermission from "../../../../utils/permission";
import ConfirmModal from "../../../../components/modals/ConfirmModal";

type LessonComment = {
  id: string;
  lessonId: string;
  sectionId?: string | null;
  userId: string;
  content: string;
  metadata?: any;
  createdAt?: string;
  user?: { firstName?: string; lastName?: string };
};

export default function LessonCommentsView({
  lessonId,
  sections,
  defaultSectionId,
  fixedScope,
}: {
  lessonId: string;
  sections: any[];
  defaultSectionId?: string;
  fixedScope?: { type: "lesson" } | { type: "section"; sectionId: string };
}) {
  const canList = checkPermission("lesson_comment_list");
  const canAdd = checkPermission("lesson_comment_add");
  const canDelete = checkPermission("lesson_comment_delete");

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<LessonComment[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | "lesson">(defaultSectionId ? defaultSectionId : "lesson");
  const [newComment, setNewComment] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const sectionMap = useMemo(() => {
    const map: Record<string, string> = {};
    (sections || []).forEach((s: any) => (map[s.id] = s.title));
    return map;
  }, [sections]);

  useEffect(() => {
    fetchComments();
  }, [lessonId, selectedSectionId, fixedScope?.type, (fixedScope as any)?.sectionId]);

  const fetchComments = async () => {
    if (!canList) return;
    setLoading(true);
    try {
      const res = await ApiService.getLessonComments(lessonId, {
        sectionId:
          fixedScope?.type === "lesson"
            ? undefined
            : fixedScope?.type === "section"
            ? fixedScope.sectionId
            : selectedSectionId === "lesson"
            ? undefined
            : (selectedSectionId as string),
        limit: 200,
        sortBy: "createdAt",
        sortOrder: "DESC",
      });
      const data: LessonComment[] = Array.isArray(res) ? res : res?.data || [];
      setItems(data);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!canAdd || !newComment.trim()) return;
    setLoading(true);
    try {
      await ApiService.createLessonComment(lessonId, {
        content: newComment.trim(),
        sectionId:
          fixedScope?.type === "lesson"
            ? undefined
            : fixedScope?.type === "section"
            ? fixedScope.sectionId
            : selectedSectionId && selectedSectionId !== "lesson"
            ? (selectedSectionId as string)
            : undefined,
      });
      setNewComment("");
      await fetchComments();
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (commentId: string) => {
    if (!canDelete) return;
    setDeleteId(commentId);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteId(null);
    setDeleteLoading(false);
  };

  const confirmDelete = async () => {
    if (!canDelete || !deleteId) return;
    setDeleteLoading(true);
    try {
      await ApiService.deleteLessonComment(lessonId, deleteId);
      await fetchComments();
      closeDelete();
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!canList) {
    return <div className="bg-white rounded-lg p-6 border text-center text-gray-600">Bu alanı görüntüleme yetkiniz yok.</div>;
  }

  const activeSectionId =
    fixedScope?.type === "section"
      ? fixedScope.sectionId
      : selectedSectionId && selectedSectionId !== "lesson"
      ? (selectedSectionId as string)
      : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-purple-600 flex-shrink-0" />
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex-shrink-0">Yorumlar</h2>
        {activeSectionId && (
          <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
            {sections?.find((s: any) => s.id === activeSectionId)?.title || "Bölüm"}
          </span>
        )}
      </div>

      {canAdd && (
        <div>
          <textarea
            className="w-full border rounded-lg px-3 py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-purple-200 min-h-[72px]"
            placeholder={
              activeSectionId
                ? `${sections?.find((s: any) => s.id === activeSectionId)?.title} bölümü için yorum yazın...`
                : "Bu bölümü için yorum yazın..."
            }
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={loading}
            rows={3}
          />
          <div className="mt-2 flex justify-end">
            <button
              className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50"
              onClick={handleCreate}
              disabled={loading || !newComment.trim()}>
              <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Gönder
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-sm sm:text-base text-gray-500">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {activeSectionId ? "Bu Bölüm İçin Henüz Yorum Bulunmuyor" : "Henüz Yorum Bulunmuyor"}
          </h3>
          <p className="text-gray-500">
            {activeSectionId
              ? "Bu bölüm için ilk yorumunuzu yazarak tartışmayı başlatın."
              : "Bu bölüm için ilk yorumunuzu yazarak başlayın."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => {
            const name = `${c.user?.firstName || "Kullanıcı"} ${c.user?.lastName || ""}`.trim();
            const when = c.createdAt ? new Date(c.createdAt).toLocaleString("tr-TR") : "";
            return (
              <div
                key={c.id}
                className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-50 hover:border-purple-300 transition-all duration-200">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  <div className="flex-1 space-y-2 min-w-0">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-900 font-medium truncate">{name}</span>
                      {c.sectionId && (
                        <span className="text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                          {sectionMap[c.sectionId] || "Bölüm"}
                        </span>
                      )}
                    </div>

                    <div className="text-sm sm:text-base text-gray-900 whitespace-pre-wrap leading-relaxed">{c.content}</div>

                    {when && (
                      <div className="flex items-center gap-1.5 text-xs sm:text-sm">
                        <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-gray-600">{when}</span>
                      </div>
                    )}
                  </div>

                  {canDelete && (
                    <div className="flex-shrink-0 self-start sm:self-auto">
                      <button
                        className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                        onClick={() => openDelete(c.id)}
                        title="Sil">
                        <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                        Sil
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ConfirmModal
        isOpen={deleteOpen}
        onClose={closeDelete}
        onConfirm={confirmDelete}
        title="Yorumu Sil"
        message="Bu yorumu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        isLoading={deleteLoading}
      />
    </div>
  );
}
